import { useState, useEffect, useRef } from "react";
import { db, auth } from "./firebase";
import {
  collection, onSnapshot, query, orderBy, where,
  addDoc, updateDoc, doc, serverTimestamp, getDocs
} from "firebase/firestore";
import QRCode from "qrcode";

const SITES = ["Paddington Square","Canary Wharf","Kings Cross","Liverpool Street","Waterloo","Victoria"];

const STATUS_COLORS = {
  expected:  { bg:"#F0F2F8", color:"#1A1A2E", label:"Expected" },
  on_site:   { bg:"#F0FFF4", color:"#2E7D52", label:"On Site"  },
  departed:  { bg:"#F4F2EE", color:"#9B8F7A", label:"Departed" },
  overstay:  { bg:"#FEF2F2", color:"#C0392B", label:"Overstay" },
};

const fmtTime = (ts) => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
  } catch { return "—"; }
};
const fmtDate = (ts) => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return "Today";
    return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short"});
  } catch { return "—"; }
};

const S = {
  card: { background:"#FFFFFF", border:"1px solid #E2DDD6", borderRadius:10, padding:16, marginBottom:12 },
  inp:  { background:"#FAFAF8", border:"1px solid #E2DDD6", color:"#1A1A2E", padding:"9px 12px", borderRadius:7, fontSize:13, width:"100%", boxSizing:"border-box" },
  sel:  { background:"#FAFAF8", border:"1px solid #E2DDD6", color:"#1A1A2E", padding:"9px 12px", borderRadius:7, fontSize:13, width:"100%" },
  btn:  (bg,col,brd) => ({ background:bg, color:col||"#fff", border:`1px solid ${brd||bg}`, padding:"8px 18px", borderRadius:7, cursor:"pointer", fontSize:12, fontWeight:700 }),
  lbl:  { fontSize:10, color:"#9B8F7A", letterSpacing:1.5, display:"block", marginBottom:5, textTransform:"uppercase", fontWeight:700 },
};

export default function VisitorManagement({ focusTerm, onFocusHandled }) {
  const [visitors, setVisitors]     = useState([]);
  const [officers, setOfficers]     = useState([]);
  const [tab, setTab]               = useState("board");
  const [showAdd, setShowAdd]       = useState(false);
  const [qrPreview, setQrPreview]   = useState(null);
  const [sending, setSending]       = useState(false);
  const [filter, setFilter]         = useState("all");
  const [search, setSearch]         = useState("");
  const tenantId = auth.currentUser?.tenantId || "osecurity-ltd";

  // Deep-link from global search: visitors have no detail panel, so surface the
  // row by jumping to the board and driving the existing search filter.
  useEffect(() => {
    if (!focusTerm) return;
    setTab("board");
    setSearch(focusTerm);
    onFocusHandled && onFocusHandled();
  }, [focusTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const [form, setForm] = useState({
    visitor_name:"", visitor_email:"", visitor_company:"",
    host_name:"", site:SITES[0], expected_arrival:"", expected_departure:"",
    purpose:"", notes:""
  });

  useEffect(() => {
    const q = query(
      collection(db,"visitors"),
      where("tenant_id","==",tenantId),
      orderBy("expected_arrival","desc")
    );
    return onSnapshot(q, snap =>
      setVisitors(snap.docs.map(d => ({ id:d.id, ...d.data() })))
    );
  }, [tenantId]);

  useEffect(() => {
    const q = query(collection(db,"users"), where("tenant_id","==",tenantId));
    return onSnapshot(q, snap =>
      setOfficers(snap.docs.map(d => ({ uid:d.id, ...d.data() })))
    );
  }, [tenantId]);

  // Auto-flag overstays
  useEffect(() => {
    const interval = setInterval(async () => {
      const now = new Date();
      for (const v of visitors) {
        if (v.status !== "on_site") continue;
        const dep = v.expected_departure?.toDate?.();
        if (dep && dep < now) {
          await updateDoc(doc(db,"visitors",v.id), { status:"overstay" });
        }
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [visitors]);

  const generateQR = async (visitorId, visitorName) => {
    const data = JSON.stringify({ visitor_id:visitorId, name:visitorName, tenant:tenantId });
    const url = await QRCode.toDataURL(data, { width:256, margin:2, color:{ dark:"#1A1A2E", light:"#FFFFFF" } });
    return url;
  };

  const registerVisitor = async () => {
    if (!form.visitor_name || !form.expected_arrival) {
      alert("Name and arrival time are required"); return;
    }
    setSending(true);
    try {
      const ref = await addDoc(collection(db,"visitors"), {
        ...form,
        expected_arrival:  new Date(form.expected_arrival),
        expected_departure: form.expected_departure ? new Date(form.expected_departure) : null,
        status:     "expected",
        tenant_id:  tenantId,
        created_by: auth.currentUser?.email || "admin",
        created_at: serverTimestamp(),
        checked_in_at:  null,
        checked_out_at: null,
        qr_scanned:     false,
      });

      // Generate QR and save
      const qrUrl = await generateQR(ref.id, form.visitor_name);
      await updateDoc(ref, { qr_code: ref.id }); // store ID as QR data

      setQrPreview({ id:ref.id, name:form.visitor_name, qrUrl, email:form.visitor_email });
      setShowAdd(false);
      setForm({ visitor_name:"", visitor_email:"", visitor_company:"", host_name:"", site:SITES[0], expected_arrival:"", expected_departure:"", purpose:"", notes:"" });
    } catch(e) { alert("Failed: "+e.message); }
    setSending(false);
  };

  const checkOut = async (id) => {
    await updateDoc(doc(db,"visitors",id), {
      status:"departed",
      checked_out_at: serverTimestamp()
    });
  };

  const filtered = visitors.filter(v => {
    const matchStatus = filter === "all" || v.status === filter;
    const matchSearch = !search || [v.visitor_name,v.visitor_company,v.host_name,v.site].some(f => f?.toLowerCase().includes(search.toLowerCase()));
    return matchStatus && matchSearch;
  });

  const stats = {
    expected: visitors.filter(v=>v.status==="expected").length,
    on_site:  visitors.filter(v=>v.status==="on_site").length,
    overstay: visitors.filter(v=>v.status==="overstay").length,
    today:    visitors.filter(v=>{ try{ const d=v.expected_arrival?.toDate?.(); return d&&d.toDateString()===new Date().toDateString(); }catch{return false;} }).length,
  };

  return (
    <div style={{ padding:"0 24px 24px", color:"#1A1A2E" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 0 16px" }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800 }}>Visitor Management</div>
          <div style={{ fontSize:11, color:"#9B8F7A", marginTop:2 }}>
            Pre-register · QR passes · Live board · Overstay alerts
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {["board","history"].map(t => (
            <button key={t} onClick={()=>setTab(t)} style={{
              background: tab===t?"#1A1A2E":"#FFFFFF", color: tab===t?"#C9A84C":"#6B6055",
              border:"1px solid #E2DDD6", padding:"6px 16px", borderRadius:7, cursor:"pointer",
              fontSize:12, fontWeight:700, textTransform:"capitalize"
            }}>{t}</button>
          ))}
          <button style={S.btn("#C9A84C","#1A1A2E","#C9A84C")} onClick={()=>setShowAdd(true)}>
            + Pre-register Visitor
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
        {[
          ["On Site",  stats.on_site,  "#2E7D52"],
          ["Expected", stats.expected, "#1A1A2E"],
          ["Overstay", stats.overstay, "#C0392B"],
          ["Today",    stats.today,    "#C9A84C"],
        ].map(([l,v,c]) => (
          <div key={l} style={{ ...S.card, marginBottom:0, borderLeft:`3px solid ${c}`, padding:"14px 16px" }}>
            <div style={{ fontSize:28, fontWeight:800, color:c }}>{v}</div>
            <div style={{ fontSize:10, color:"#9B8F7A", letterSpacing:1.5, textTransform:"uppercase", marginTop:3 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* QR Preview modal */}
      {qrPreview && (
        <div style={{ position:"fixed", inset:0, background:"rgba(26,26,46,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#FFFFFF", borderRadius:14, padding:32, width:360, textAlign:"center" }}>
            <div style={{ fontSize:17, fontWeight:800, color:"#1A1A2E", marginBottom:4 }}>Visitor Pass Ready</div>
            <div style={{ fontSize:12, color:"#9B8F7A", marginBottom:20 }}>{qrPreview.name}</div>
            <img src={qrPreview.qrUrl} alt="QR Code" style={{ width:200, height:200, borderRadius:12, border:"1px solid #E2DDD6" }}/>
            <div style={{ fontSize:11, color:"#9B8F7A", margin:"12px 0 20px" }}>
              Guard scans this at reception to check in the visitor
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button style={{ ...S.btn("#F4F2EE","#6B6055","#E2DDD6"), flex:1 }} onClick={()=>setQrPreview(null)}>Close</button>
              <a href={qrPreview.qrUrl} download={`visitor-pass-${qrPreview.name}.png`}
                style={{ ...S.btn("#1A1A2E","#C9A84C","#1A1A2E"), flex:2, textDecoration:"none", display:"flex", alignItems:"center", justifyContent:"center" }}>
                Download Pass
              </a>
            </div>
          </div>
        </div>
      )}

      {tab === "board" && (
        <>
          {/* Filter bar */}
          <div style={{ display:"flex", gap:10, marginBottom:14, alignItems:"center" }}>
            <input style={{ ...S.inp, maxWidth:260 }} placeholder="Search visitor, host, site..." value={search} onChange={e=>setSearch(e.target.value)}/>
            <div style={{ display:"flex", gap:6 }}>
              {["all","expected","on_site","overstay","departed"].map(f => (
                <button key={f} onClick={()=>setFilter(f)} style={{
                  background: filter===f?"#1A1A2E":"#FFFFFF",
                  color: filter===f?"#C9A84C":"#6B6055",
                  border:"1px solid #E2DDD6", padding:"5px 12px", borderRadius:20,
                  cursor:"pointer", fontSize:11, fontWeight:700,
                  textTransform:"capitalize"
                }}>{f.replace("_"," ")}</button>
              ))}
            </div>
          </div>

          {/* Visitor board */}
          <div style={S.card}>
            {filtered.length === 0 ? (
              <div style={{ textAlign:"center", padding:48, color:"#9B8F7A" }}>
                {visitors.length === 0 ? "No visitors registered yet." : "No visitors match this filter."}
              </div>
            ) : (
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:"#F8F7F4" }}>
                    {["Visitor","Company","Host","Site","Expected","Status","Actions"].map(h => (
                      <th key={h} style={{ fontSize:10, color:"#9B8F7A", letterSpacing:1.5, textTransform:"uppercase", padding:"10px 12px", borderBottom:"1px solid #EAE6DE", textAlign:"left", fontWeight:700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(v => {
                    const sc = STATUS_COLORS[v.status] || STATUS_COLORS.expected;
                    return (
                      <tr key={v.id} style={{ borderBottom:"1px solid #F4F0E8" }}>
                        <td style={{ padding:"11px 12px" }}>
                          <div style={{ fontSize:13, fontWeight:700, color:"#1A1A2E" }}>{v.visitor_name}</div>
                          {v.visitor_email && <div style={{ fontSize:10, color:"#9B8F7A" }}>{v.visitor_email}</div>}
                        </td>
                        <td style={{ padding:"11px 12px", fontSize:12, color:"#6B6055" }}>{v.visitor_company||"—"}</td>
                        <td style={{ padding:"11px 12px", fontSize:12, color:"#1A1A2E" }}>{v.host_name||"—"}</td>
                        <td style={{ padding:"11px 12px", fontSize:12, color:"#6B6055" }}>{v.site}</td>
                        <td style={{ padding:"11px 12px" }}>
                          <div style={{ fontSize:12, color:"#1A1A2E" }}>{fmtDate(v.expected_arrival)}</div>
                          <div style={{ fontSize:10, color:"#9B8F7A" }}>{fmtTime(v.expected_arrival)}</div>
                        </td>
                        <td style={{ padding:"11px 12px" }}>
                          <span style={{ background:sc.bg, color:sc.color, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>{sc.label}</span>
                        </td>
                        <td style={{ padding:"11px 12px" }}>
                          <div style={{ display:"flex", gap:6 }}>
                            {v.status === "on_site" && (
                              <button style={S.btn("#FEF2F2","#C0392B","#FECACA")} onClick={()=>checkOut(v.id)}>Check out</button>
                            )}
                            {v.qr_code && (
                              <button style={S.btn("#F4F2EE","#6B6055","#E2DDD6")} onClick={async()=>{
                                const url = await QRCode.toDataURL(JSON.stringify({visitor_id:v.id,name:v.visitor_name,tenant:tenantId}),{width:256,margin:2,color:{dark:"#1A1A2E",light:"#FFFFFF"}});
                                setQrPreview({id:v.id,name:v.visitor_name,qrUrl:url,email:v.visitor_email});
                              }}>QR</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === "history" && (
        <div style={S.card}>
          <div style={{ fontSize:13, fontWeight:700, color:"#1A1A2E", marginBottom:14 }}>Visitor History</div>
          {visitors.filter(v=>v.status==="departed").length === 0
            ? <div style={{ textAlign:"center", padding:48, color:"#9B8F7A" }}>No completed visits yet.</div>
            : visitors.filter(v=>v.status==="departed").map(v => (
              <div key={v.id} style={{ display:"flex", gap:12, padding:"12px 0", borderBottom:"1px solid #F4F0E8", alignItems:"center" }}>
                <div style={{ width:36, height:36, borderRadius:10, background:"#F4F2EE", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:"#9B8F7A", flexShrink:0 }}>
                  {v.visitor_name?.[0]?.toUpperCase()||"?"}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#1A1A2E" }}>{v.visitor_name}</div>
                  <div style={{ fontSize:11, color:"#9B8F7A" }}>
                    {v.visitor_company} · Host: {v.host_name||"—"} · {v.site}
                  </div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:11, color:"#6B6055" }}>In: {fmtTime(v.checked_in_at)}</div>
                  <div style={{ fontSize:11, color:"#6B6055" }}>Out: {fmtTime(v.checked_out_at)}</div>
                  <div style={{ fontSize:10, color:"#9B8F7A" }}>{fmtDate(v.expected_arrival)}</div>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* Add visitor modal */}
      {showAdd && (
        <div style={{ position:"fixed", inset:0, background:"rgba(26,26,46,0.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#FFFFFF", borderRadius:14, padding:28, width:520, maxHeight:"85vh", overflowY:"auto", boxSizing:"border-box" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <div style={{ fontSize:17, fontWeight:800, color:"#1A1A2E" }}>Pre-register Visitor</div>
              <button onClick={()=>setShowAdd(false)} style={{ background:"none", border:"1px solid #E2DDD6", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:13, color:"#9B8F7A" }}>Close</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.lbl}>Visitor Name *</label>
                <input style={S.inp} placeholder="Full name" value={form.visitor_name} onChange={e=>setForm(f=>({...f,visitor_name:e.target.value}))}/>
              </div>
              <div>
                <label style={S.lbl}>Email</label>
                <input style={S.inp} type="email" placeholder="visitor@company.com" value={form.visitor_email} onChange={e=>setForm(f=>({...f,visitor_email:e.target.value}))}/>
              </div>
              <div>
                <label style={S.lbl}>Company</label>
                <input style={S.inp} placeholder="Company name" value={form.visitor_company} onChange={e=>setForm(f=>({...f,visitor_company:e.target.value}))}/>
              </div>
              <div>
                <label style={S.lbl}>Host Name</label>
                <select style={S.sel} value={form.host_name} onChange={e=>setForm(f=>({...f,host_name:e.target.value}))}>
                  <option value="">Select host...</option>
                  {officers.map(o => <option key={o.uid} value={o.name||o.email}>{o.name||o.email}</option>)}
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={S.lbl}>Site</label>
                <select style={S.sel} value={form.site} onChange={e=>setForm(f=>({...f,site:e.target.value}))}>
                  {SITES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={S.lbl}>Expected Arrival *</label>
                <input type="datetime-local" style={S.inp} value={form.expected_arrival} onChange={e=>setForm(f=>({...f,expected_arrival:e.target.value}))}/>
              </div>
              <div>
                <label style={S.lbl}>Expected Departure</label>
                <input type="datetime-local" style={S.inp} value={form.expected_departure} onChange={e=>setForm(f=>({...f,expected_departure:e.target.value}))}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.lbl}>Purpose of Visit</label>
                <input style={S.inp} placeholder="e.g. Client meeting, Site inspection" value={form.purpose} onChange={e=>setForm(f=>({...f,purpose:e.target.value}))}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.lbl}>Notes</label>
                <textarea style={{ ...S.inp, minHeight:60, resize:"vertical" }} placeholder="Any additional notes..." value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
              </div>
            </div>
            <div style={{ display:"flex", gap:10, marginTop:22 }}>
              <button style={{ ...S.btn("#FFFFFF","#6B6055","#E2DDD6"), flex:1 }} onClick={()=>setShowAdd(false)}>Cancel</button>
              <button style={{ ...S.btn("#C9A84C","#1A1A2E","#C9A84C"), flex:2, opacity:sending?0.6:1 }} onClick={registerVisitor} disabled={sending}>
                {sending ? "Registering..." : "Register & Generate QR Pass"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
